import { useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { Message, Venue } from '@/src/lib/types';
import MessageBubble from './message-bubble';
import Welcome from './welcome';
import TypingDots from './typing-dots';
import Composer from './composer';
import { useAppColors } from '@/src/lib/theme';

interface ChatProps {
  venue: Venue;
  messages: Message[];
  onSendMessage: (text: string) => void;
  isStreaming: boolean;
  onStopStreaming: () => void;
  activeDrawerId: string;
  onToolDecision: (messageId: string, confirmed: boolean) => void;
  onOpenPanel?: () => void;
  openPanelLabel?: string;
  userId?: string;
  userRole?: string;
  onNewChat?: () => void;
  // What the assistant is busy with right now, e.g. "Writing the quiz…".
  streamingStatus?: string | null;
}

export default function Chat({
  venue,
  messages,
  onSendMessage,
  isStreaming,
  onStopStreaming,
  activeDrawerId,
  onToolDecision,
  onOpenPanel,
  openPanelLabel,
  userId,
  userRole,
  onNewChat,
  streamingStatus,
}: ChatProps) {
  const c = useAppColors();
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isStreaming]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-hide">
        {messages.length === 0 ? (
          <Welcome
            firstName={venue.ownerName.split(' ')[0]}
            venueName={venue.name}
            timezone={venue.timezone}
            drawerId={activeDrawerId}
            venueId={venue.id}
            userId={userId}
            onSelectSuggestion={onSendMessage}
            composer={
              <motion.div layoutId="opsalo-composer" className="w-full" transition={{ type: 'spring', stiffness: 260, damping: 30 }}>
                <Composer centered onSend={onSendMessage} isStreaming={isStreaming} onStop={onStopStreaming} />
              </motion.div>
            }
            onOpenPanel={onOpenPanel}
            openPanelLabel={openPanelLabel}
            userRole={userRole}
          />
        ) : (
          <div className="px-4 lg:px-8 max-w-3xl mx-auto w-full pt-4 pb-32">
            {onOpenPanel && openPanelLabel && (
              <div className="flex justify-center mb-8">
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={onOpenPanel}
                  className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors"
                  style={{
                    background: c.iconBg,
                    border: `1px solid ${c.border}`,
                    color: c.textDim,
                  }}
                >
                  {openPanelLabel}
                </motion.button>
              </div>
            )}

            {messages.map((m, i) => {
              // Don't draw a reply bubble until it has text; the typing dots cover the wait.
              const isEmptyAssistantPlaceholder =
                m.role === 'assistant' && !m.content?.trim() && !m.isToolCall;
              if (isEmptyAssistantPlaceholder) return null;
              return (
                <MessageBubble
                  key={m.id}
                  message={m}
                  isLast={i === messages.length - 1}
                  onToolDecision={onToolDecision}
                  // Chips only on the latest reply, and not while it is still being written.
                  onSuggestionClick={i === messages.length - 1 && !isStreaming ? onSendMessage : undefined}
                />
              );
            })}

            {isStreaming && (
              <div className="flex items-center gap-3 justify-start mb-6">
                <TypingDots />
                {streamingStatus && (
                  <span className="text-sm" style={{ color: c.textDim }}>{streamingStatus}</span>
                )}
              </div>
            )}

            <div ref={bottomRef} className="h-4" />
          </div>
        )}
      </div>

      {/* Once the chat starts, the message box moves (animates) down to the bottom. */}
      {messages.length > 0 && (
        <motion.div layoutId="opsalo-composer" className="flex-shrink-0" transition={{ type: 'spring', stiffness: 260, damping: 30 }}>
          <Composer onSend={onSendMessage} isStreaming={isStreaming} onStop={onStopStreaming} onClear={onNewChat} />
        </motion.div>
      )}
    </div>
  );
}